import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthStorageService } from 'src/app/User/authStorage.service';

/** Outcome of probing for the Swagger document. */
export type SwaggerAvailability =
    /** Still probing. */
    'checking'
    /** The Swagger document was served. */
    | 'available'
    /** The dev server answered with the SPA shell, so /swagger is not proxied to the API. */
    | 'notProxied'
    /** Swagger is not served here, which is expected in production. */
    | 'unavailable'
    /** Swagger is served but the user has no token to hand to it. */
    | 'noToken';

/**
 * Hosts the API's Swagger UI inside the Diagnostics area with the signed in user's bearer token
 * already applied, so secured endpoints can be exercised without pasting a token.
 *
 * The token lives in memory in AuthStorageService rather than in a cookie, so the Swagger page
 * cannot read it on its own. It is handed over in the URL fragment, which browsers never send to
 * the server, and the injected swaggerAuth.js script clears it from the frame's address as soon as
 * it has been read.
 *
 * The frame is always same-origin: deployed environments serve the SPA and /swagger from the same
 * host, and local development proxies /swagger to the API. That satisfies the X-Frame-Options
 * SAMEORIGIN header the API sends, so no security header has to be relaxed to embed it.
 *
 * Swagger is only registered in development (see Startup.SwaggerIsEnabled), so this page
 * explains why it is unavailable rather than showing an empty frame. The Diagnostics menu hides
 * the entry entirely elsewhere; this page still has to cope with someone deep-linking to it.
 */
@Component({
    selector: 'swagger-diagnostic',
    templateUrl: './swaggerDiagnostic.component.html',
    styleUrls: ['./swaggerDiagnostic.component.scss'],
    standalone: false
})
export class SwaggerDiagnosticComponent implements OnInit {
    constructor(
        private readonly _authStorageService: AuthStorageService,
        private readonly _sanitizer: DomSanitizer
    ) {
    }

    readonly swaggerUiUrl: string = '/swagger/ui/index';
    readonly swaggerDocsUrl: string = '/swagger/docs/v1';

    availability: SwaggerAvailability = 'checking';
    swaggerFrameUrl: SafeResourceUrl;

    /** True until the embedded Swagger UI has actually rendered its operation list. */
    frameLoading: boolean = true;
    frameLoadingMessage: string = 'Loading the API definition\u2026';

    /** Identifies the current load so timers from a superseded load cannot clear its state. */
    private _loadSequence: number = 0;

    async ngOnInit(): Promise<void> {
        await this.load();
    }

    async load(): Promise<void> {
        const sequence = ++this._loadSequence;

        this.availability = 'checking';
        this.swaggerFrameUrl = null;
        this.frameLoading = true;
        this.frameLoadingMessage = 'Loading the API definition\u2026';

        try {
            // HEAD avoids pulling the multi-megabyte document just to see whether it is served.
            const response = await fetch(this.swaggerDocsUrl, { method: 'HEAD' });

            if (!response.ok) {
                this.availability = 'unavailable';
                return;
            }

            // The dev server falls back to index.html for unknown paths, so a 200 alone is not proof
            // that the API answered. Without this check the frame would just load the SPA again.
            // The real document is always JSON.
            const contentType = response.headers.get('content-type') || '';

            if (!contentType.toLowerCase().includes('application/json')) {
                this.availability = 'notProxied';
                return;
            }
        } catch {
            this.availability = 'unavailable';
            return;
        }

        const token = this._authStorageService.getAuthData()?.access_token;

        if (!token) {
            this.availability = 'noToken';
            return;
        }

        this.swaggerFrameUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
            `${this.swaggerUiUrl}#access_token=${encodeURIComponent(token)}`);
        this.availability = 'available';

        // Safety net: if the frame's load event never arrives the poll below never starts, and a
        // stuck spinner is worse than an unstyled frame.
        setTimeout(() => {
            if (sequence === this._loadSequence) {
                this.frameLoading = false;
            }
        }, 120000);
    }

    openInNewTab(): void {
        const token = this._authStorageService.getAuthData()?.access_token;
        const fragment = token ? `#access_token=${encodeURIComponent(token)}` : '';

        window.open(`${this.swaggerUiUrl}${fragment}`, '_blank', 'noopener');
    }

    /**
     * The iframe's load event only means the Swagger UI *page* arrived. It then fetches a ~2.5 MB
     * document and renders roughly 190 controller groups, which takes several seconds and leaves an
     * empty frame behind. Keep the loader up until operations are actually on screen.
     */
    onFrameLoaded(frame: HTMLIFrameElement): void {
        const sequence = this._loadSequence;
        const startedAt = Date.now();
        const timeoutMs = 120000;

        const poll = () => {
            if (!this.frameLoading || sequence !== this._loadSequence) {
                return;
            }

            let rendered: boolean;

            try {
                // Same-origin in every environment, so the frame's DOM is readable. If that ever
                // stops being true, stop waiting rather than showing the loader forever.
                rendered = frame.contentDocument.querySelectorAll('.resource').length > 0;
            } catch {
                this.frameLoading = false;
                return;
            }

            const elapsed = Date.now() - startedAt;

            if (rendered || elapsed > timeoutMs) {
                this.frameLoading = false;
                return;
            }

            if (elapsed > 5000) {
                this.frameLoadingMessage =
                    'Still loading \u2014 the API definition is large, and the first load of the day is the slowest\u2026';
            }

            setTimeout(poll, 250);
        };

        poll();
    }
}
